// InterviewDashboard.jsx

import React, { useMemo, useState } from "react";
import {
    Modal,
    Button,
    Pagination,
    Flex,
} from "antd";

import { createUseStyles } from "react-jss";
import { useRouter } from "next/router";

import { UIAvatar, UIHeading } from "../ui/UIElements";
import UIAntText from "../ui/common/UIAntText";
import UITag from "../ui/common/UITag";

import { pascalCase } from "../common/PascalCase";
import { Date } from "../ui/forms/UIObjects";
import { TimeFormat } from "@/helpers/TimeFormat";
import SafeFontAwesomeIcon from "../SafeFontAwesomeIcon";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";

const InterviewDashboard = ({
    records = [],
    changeDate,
    defaultDate,
}) => {
    const styles = useStyles();
    const router = useRouter();

    const [isModalVisible, setIsModalVisible] =
        useState(false);

    const [page, setPage] = useState(1);

    const pageSize = 5;

    const interviewStatus = {
        0: "Scheduled",
        1: "Completed",
        2: "Invited",
        3: "Accepted",
        4: "Rejected",
        5: "Cancelled",
        6: "Completed",
    };

    const statusStyles = {
        0: {
            background: "#eff6ff",
            color: "#2364ae",
        },
        1: {
            background: "#ecfdf3",
            color: "#16a34a",
        },
        2: {
            background: "#fffbeb",
            color: "#d97706",
        },
        3: {
            background: "#ecfeff",
            color: "#0891b2",
        },
        4: {
            background: "#fef2f2",
            color: "#dc2626",
        },
        5: {
            background: "#f1f5f9",
            color: "#475569",
        },
        6: {
            background: "#ecfdf3",
            color: "#16a34a",
        },
    };

    const emptyText = useMemo(() => {
        const dayLabel = defaultDate
            ? TimeFormat(defaultDate, "DD MMM YYYY")
            : "Today";

        return `No appointments scheduled for ${dayLabel}`;
    }, [defaultDate]);

    const topTwo = records.slice(0, 2);

    const modalPageItems = useMemo(() => {
        const start = (page - 1) * pageSize;

        return records.slice(start, start + pageSize);
    }, [page, records]);

    const goToCandidate = (item) => {
        router.push({
            pathname: `/employer/candidates/${item?.DataFields?.MemberJobHistorySlug}`,
            query: {
                q: "Interviews",
                pg: 1,
            },
        });
    };

    const InterviewCard = ({
        item,
        showStatus = true,
    }) => {
        const candidate =
            item?.DataFields?.Candidate || {};

        const status =
            item?.DataFields?.Status;

        return (
            <div className={styles.interviewCard}>
                <div className={styles.cardLeft}>
                    <UIAvatar
                        size={52}
                        src={candidate?.ProfilePhoto}
                        name={pascalCase(
                            `${candidate?.FirstName || ""} ${candidate?.LastName || ""
                            }`
                        )}
                    />

                    <div className={styles.cardContent}>
                        <button
                            className={styles.titleButton}
                            onClick={() => goToCandidate(item)}
                        >
                            {item?.DataFields?.InterviewTitle}
                        </button>

                        <UIAntText
                            type="secondary"
                            className={styles.subText}
                        >
                            Interview with{" "}
                            <strong>
                                {candidate?.FirstName}{" "}
                                {candidate?.LastName}
                            </strong>
                        </UIAntText>

                        <div className={styles.timeRow}>
                            <span>
                                {TimeFormat(
                                    item?.DataFields?.Start,
                                    "MMM DD YYYY"
                                )}
                            </span>

                            <span className={styles.dot} />

                            <span>
                                {TimeFormat(
                                    item?.DataFields?.Start,
                                    "hh:mm A"
                                )}
                            </span>
                        </div>

                        {showStatus && (
                            <UITag
                                name={interviewStatus[status]}
                                style={{
                                    width: "fit-content",
                                    borderRadius: 999,
                                    border: "none",
                                    padding: "4px 10px",
                                    fontSize: 12,
                                    fontWeight: 700,
                                    marginTop: 10,
                                    ...statusStyles[status],
                                }}
                            />
                        )}
                    </div>
                </div>

                {item?.DataFields?.AdditionalInfo
                    ?.hangoutLink && (
                        <Button
                            type="primary"
                            className={styles.joinButton}
                            onClick={() =>
                                window.open(
                                    item?.DataFields?.AdditionalInfo
                                        ?.hangoutLink,
                                    "_blank"
                                )
                            }
                        >
                            Join
                        </Button>
                    )}
            </div>
        );
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div>
                    <UIHeading
                        level={4}
                        style={{ margin: 0 }}
                    >
                        My Appointments
                    </UIHeading>

                    <p className={styles.headerSubtext}>
                        Track upcoming interviews and
                        meetings
                    </p>
                </div>

                <Date
                    onChange={changeDate}
                    defaultValue={defaultDate}
                    monthFormat="MMM DD YYYY"
                />
            </div>

            <div className={styles.body}>
                {topTwo.length === 0 ? (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>
                            <SafeFontAwesomeIcon
                                icon={faCalendarAlt}
                                color="#2364ae"
                            />
                        </div>

                        <p className={styles.emptyText}>
                            {emptyText}
                        </p>
                    </div>
                ) : (
                    topTwo.map((item, idx) => (
                        <InterviewCard
                            key={idx}
                            item={item}
                            showStatus
                        />
                    ))
                )}
            </div>

            {records.length > 2 && (
                <Flex justify="center">
                    <Button
                        type="link"
                        className={styles.viewMoreBtn}
                        onClick={() =>
                            setIsModalVisible(true)
                        }
                    >
                        View All Interviews
                    </Button>
                </Flex>
            )}

            <Modal
                title={
                    <div className={styles.modalTitle}>
                        All Interviews
                    </div>
                }
                open={isModalVisible}
                footer={null}
                width={720}
                onCancel={() =>
                    setIsModalVisible(false)
                }
                afterOpenChange={(open) => {
                    if (open) setPage(1);
                }}
            >
                <div className={styles.modalBody}>
                    {records.length === 0 ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIcon}>
                                📅
                            </div>

                            <p className={styles.emptyText}>
                                {emptyText}
                            </p>
                        </div>
                    ) : (
                        modalPageItems.map((item, idx) => (
                            <InterviewCard
                                key={idx}
                                item={item}
                                showStatus={false}
                            />
                        ))
                    )}
                </div>

                {records.length > pageSize && (
                    <Flex
                        justify="center"
                        style={{ marginTop: 20 }}
                    >
                        <Pagination
                            current={page}
                            pageSize={pageSize}
                            total={records.length}
                            onChange={(p) => setPage(p)}
                            showSizeChanger={false}
                        />
                    </Flex>
                )}
            </Modal>
        </div>
    );
};

export default InterviewDashboard;

const useStyles = createUseStyles({
    wrapper: {
        padding: 24,

        "@media (max-width: 768px)": {
            padding: 18,
        },
    },

    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 24,

        "@media (max-width: 640px)": {
            alignItems: "flex-start",
            flexDirection: "column",
        },
    },

    headerSubtext: {
        margin: "6px 0 0",
        fontSize: 13,
        color: "#64748b",
        fontWeight: 500,
    },

    body: {
        display: "flex",
        flexDirection: "column",
        gap: 14,
    },

    interviewCard: {
        border: "1px solid #e5e7eb",
        borderRadius: 20,
        padding: 18,
        background: "#ffffff",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        transition: "all 0.22s ease",

        "&:hover": {
            borderColor: "#dbeafe",
            background: "#fcfdff",
            boxShadow:
                "0 10px 24px rgba(37,99,235,0.08)",
            transform: "translateY(-1px)",
        },

        "@media (max-width: 640px)": {
            alignItems: "flex-start",
            flexDirection: "column",
        },
    },

    cardLeft: {
        display: "flex",
        alignItems: "flex-start",
        gap: 14,
        flex: 1,
        minWidth: 0,
    },

    cardContent: {
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
    },

    titleButton: {
        border: "none",
        background: "transparent",
        padding: 0,
        textAlign: "left",
        cursor: "pointer",
        fontSize: 16,
        fontWeight: 700,
        color: "#0f172a",
        marginBottom: 4,

        "&:hover": {
            color: "#2364ae",
        },
    },

    subText: {
        fontSize: 13,
        lineHeight: 1.5,
    },

    timeRow: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        marginTop: 10,
        color: "#475569",
        fontSize: 13,
        fontWeight: 600,
        flexWrap: "wrap",
    },

    dot: {
        width: 4,
        height: 4,
        borderRadius: "50%",
        background: "#94a3b8",
    },

    joinButton: {
        borderRadius: 12,
        height: 42,
        padding: "0 18px",
        fontWeight: 700,
        boxShadow: "none",
    },

    viewMoreBtn: {
        marginTop: 18,
        fontWeight: 700,
    },

    emptyState: {
        minHeight: 220,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
    },

    emptyIcon: {
        fontSize: 42,
        marginBottom: 12,
    },

    emptyText: {
        fontSize: 15,
        color: "#64748b",
        fontWeight: 500,
        margin: 0,
    },

    modalTitle: {
        fontSize: 18,
        fontWeight: 800,
        color: "#0f172a",
    },

    modalBody: {
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 10,
    },
});